import type { SamlSsoRecovery } from '@/utils/github/saml-sso';
import type { RestEndpointMethodTypes } from '@octokit/plugin-rest-endpoint-methods';
import type { GetResponseDataTypeFromEndpointMethod } from '@octokit/types';
import type { Octokit } from 'octokit';

export type GithubCommits = GetResponseDataTypeFromEndpointMethod<
  typeof Octokit.prototype.request<'GET /repos/{owner}/{repo}/pulls/{pull_number}/commits'>
>;
export type Reviews = GetResponseDataTypeFromEndpointMethod<
  typeof Octokit.prototype.request<'GET /repos/{owner}/{repo}/pulls/{pull_number}/reviews'>
>;
export type ReviewComments = PullRequestComments;

export type IssueComments = GetResponseDataTypeFromEndpointMethod<
  typeof Octokit.prototype.request<'GET /repos/{owner}/{repo}/issues/{issue_number}/comments'>
>;

export type SearchCodeResponse = RestEndpointMethodTypes['search']['code']['response'];

export type GithubPullRequests = GetResponseDataTypeFromEndpointMethod<
  typeof Octokit.prototype.request<'GET /repos/{owner}/{repo}/pulls'>
>[number];

export type RepoComment = GetResponseDataTypeFromEndpointMethod<
  typeof Octokit.prototype.request<'GET /repos/{owner}/{repo}/pulls/comments'>
>[number];

export type SingleRepoComment = GetResponseDataTypeFromEndpointMethod<
  typeof Octokit.prototype.request<'GET /repos/{owner}/{repo}/pulls/comments/{comment_id}'>
>;

export type GithubPullRequest = GetResponseDataTypeFromEndpointMethod<
  typeof Octokit.prototype.request<'GET /repos/{owner}/{repo}/pulls/{pull_number}'>
>;

export type GithubRepository = GetResponseDataTypeFromEndpointMethod<
  typeof Octokit.prototype.request<'GET /repos/{owner}/{repo}'>
>;

export type PullRequestComments = GetResponseDataTypeFromEndpointMethod<
  typeof Octokit.prototype.request<'GET /repos/{owner}/{repo}/pulls/{pull_number}/comments'>
>;

export type Repository = GetResponseDataTypeFromEndpointMethod<
  typeof Octokit.prototype.request<'GET /repos/{owner}/{repo}'>
>;

export type GitHubContent = GetResponseDataTypeFromEndpointMethod<
  typeof Octokit.prototype.request<'GET /repos/{owner}/{repo}/contents/{path}'>
>;

export type GithubFile = GetResponseDataTypeFromEndpointMethod<
  typeof Octokit.prototype.request<'GET /repos/{owner}/{repo}/pulls/{pull_number}/files'>
>[number];

export type CommitCheckSuites = GetResponseDataTypeFromEndpointMethod<
  typeof Octokit.prototype.request<'GET /repos/{owner}/{repo}/commits/{ref}/check-suites'>
>['check_suites'];

export type CommitCheckRuns = GetResponseDataTypeFromEndpointMethod<
  typeof Octokit.prototype.request<'GET /repos/{owner}/{repo}/commits/{ref}/check-runs'>
>;

export type CommitCombinedStatus = GetResponseDataTypeFromEndpointMethod<
  typeof Octokit.prototype.request<'GET /repos/{owner}/{repo}/commits/{ref}/status'>
>;

export type CheckRuns = GetResponseDataTypeFromEndpointMethod<
  typeof Octokit.prototype.request<'GET /repos/{owner}/{repo}/check-suites/{check_suite_id}/check-runs'>
>;

export type CheckRunAnnotations = GetResponseDataTypeFromEndpointMethod<
  typeof Octokit.prototype.request<'GET /repos/{owner}/{repo}/check-runs/{check_run_id}/annotations'>
>;

export type Review = Reviews[number];

export type Collaborators = GetResponseDataTypeFromEndpointMethod<
  typeof Octokit.prototype.request<'GET /repos/{owner}/{repo}/collaborators'>
>;

export type Reactions = GetResponseDataTypeFromEndpointMethod<
  typeof Octokit.prototype.request<'GET /repos/{owner}/{repo}/issues/comments/{comment_id}/reactions'>
>;

export type BranchRules = GetResponseDataTypeFromEndpointMethod<
  typeof Octokit.prototype.request<'GET /repos/{owner}/{repo}/rules/branches/{branch}'>
>;

export type BranchProtections = GetResponseDataTypeFromEndpointMethod<
  typeof Octokit.prototype.request<'GET /repos/{owner}/{repo}/branches/{branch}/protection'>
>;

export type GithubTimelineItem = GetResponseDataTypeFromEndpointMethod<
  typeof Octokit.prototype.request<'GET /repos/{owner}/{repo}/issues/{issue_number}/timeline'>
>[number];

export type Reaction = Reactions[number];

export type ReactionsByCommentId = Record<number, Reactions>;

export type GithubInstallation = GetResponseDataTypeFromEndpointMethod<
  typeof Octokit.prototype.request<'GET /user/installations'>
>['installations'][number];

/**
 * Represents an installation as stored in our database and returned by our API
 */
export type DatabaseInstallation = {
  id: string;
  installationId: number;
  accountId?: number;
  accountLogin: string;
  accountType: string;
  htmlUrl: string;
  avatarUrl: string | null;
  stripeAutomaticallyAssignAndRemoveSeats: boolean;
  stripeAutomaticallyAddSeatsOnUsageLimit: boolean;
};

export type InstallationRepositories = GetResponseDataTypeFromEndpointMethod<
  typeof Octokit.prototype.request<'GET /user/installations/{installation_id}/repositories'>
>;

/**
 * Extended response type that includes sync status.
 * Used when returning repository data with background sync in progress.
 */
export type InstallationRepositoriesResponse = InstallationRepositories & {
  /** True when a background sync is in progress */
  syncing?: boolean;
  /** Present when GitHub requires SAML SSO before Cubic can refresh this user's repo access */
  samlSso?: SamlSsoRecovery;
};

export type DiscussionData = {
  timeline: GithubTimelineItem[];
  reviews: Reviews;
  commits: GithubCommits;
  reviewComments: ReviewComments;
  issueComments: IssueComments;
  commitCheckSuites: CommitCheckSuites;
  reactions: ReactionsByCommentId;
  pr: GithubPullRequest;
};

export const ReviewType = {
  COMMENT: 'COMMENT',
  APPROVE: 'APPROVE',
  REQUEST_CHANGES: 'REQUEST_CHANGES',
} as const;

export const ReviewerState = {
  COMMENTED: 'COMMENTED',
  APPROVED: 'APPROVED',
  CHANGES_REQUESTED: 'CHANGES_REQUESTED',
  REQUESTED_REVIEW: 'REQUESTED_REVIEW',
  DISMISSED: 'DISMISSED',
} as const;

export type ReviewerStateValues = (typeof ReviewerState)[keyof typeof ReviewerState];

export type Reviewer = {
  login: string;
  avatarUrl: string;
  state: ReviewerStateValues;
  submitted_at?: string;
  reviewId?: string;
};

export type ReviewerSelection = Pick<Collaborators[number], 'login' | 'avatar_url'>;

export type OrgTeams = GetResponseDataTypeFromEndpointMethod<
  typeof Octokit.prototype.request<'GET /orgs/{org}/teams'>
>;

/** Minimal team identity for requesting/removing team reviewers (GitHub API takes slugs) */
export type TeamReviewerSelection = {
  slug: string;
  name: string;
};

export const FILE_STATUS = {
  ADDED: 'added',
  MODIFIED: 'modified',
  REMOVED: 'removed',
  RENAMED: 'renamed',
} as const;
export type FileStatus = (typeof FILE_STATUS)[keyof typeof FILE_STATUS];

export type File = {
  sha: string;
  filename: string;
  previous_filename?: string;
  status: FileStatus;
  additions: number;
  deletions: number;
  changes: number;
  blob_url: string;
  raw_url: string;
  contents_url: string;
  patch?: string;
};

export type CodeContent = GithubFile & {
  oldContent?: string | null;
  newContent?: string | null;
  isLargeFile?: boolean;
  isBinaryFile?: boolean;
  isImage?: boolean;
  /**
   * True when old/new contents were stripped from a response payload because the matching
   * fileDiff entry already covers rendering. Fetch per-file contents on demand when needed.
   */
  contentsOmitted?: boolean;
};

export type ReviewTypeKeys = keyof typeof ReviewType;
export type ReviewTypeValues = (typeof ReviewType)[ReviewTypeKeys];

export type GithubUser = GetResponseDataTypeFromEndpointMethod<
  typeof Octokit.prototype.request<'GET /users/{username}'>
>;
